// @flow

import { Menu, MenuItem, MenuDivider } from '@blueprintjs/core';
import { Popover2 } from '@blueprintjs/popover2';

// Item descriptors are caller-supplied; future features add/disable items via config.
export type ContextMenuItemT = {
  key: string,
  text?: string, // omitted when divider is true
  icon?: string,
  intent?: string, // e.g. Intent.DANGER for Delete
  disabled?: boolean, // R12 — renders inactive
  divider?: boolean, // renders a MenuDivider instead of an item
  onClick?: () => void,
};

type Props = {
  items: ContextMenuItemT[],
  isOpen: boolean,
  onClose: () => void,
  children: React$Node, // the popover target (the ellipsis icon)
};

const ContextMenu = ({ items, isOpen, onClose, children }: Props) => (
  <Popover2
    isOpen={isOpen}
    onInteraction={(next) => {
      if (!next) onClose();
    }}
    placement="bottom-start"
    minimal
    content={
      // Popover2 portals the content, but React portal events still bubble
      // through the REACT tree — i.e. up to whatever row/element hosts this
      // component. Stop everything at the boundary so embedding inside
      // clickable rows is safe.
      <div onClick={(e) => e.stopPropagation()}>
        <Menu>
          {items.map((item) =>
            item.divider ? (
              <MenuDivider key={item.key} />
            ) : (
              <MenuItem
                key={item.key}
                text={item.text}
                icon={item.icon}
                intent={item.intent}
                disabled={item.disabled}
                // The explicit onClose() is load-bearing: core MenuItem's default
                // shouldDismissPopover emits the legacy bp3-popover-dismiss class,
                // which @blueprintjs/popover2 does not honour. Close first, then act.
                onClick={() => {
                  onClose();
                  if (item.onClick) item.onClick();
                }}
              />
            ),
          )}
        </Menu>
      </div>
    }
  >
    {children}
  </Popover2>
);

export default ContextMenu;
